import { LightMode } from "@/components/ui/color-mode";
import { AppColors } from "@/styles/colors";
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Portal,
  Text,
} from "@chakra-ui/react";
import { Upload, X } from "lucide-react";
import { Dispatch, DragEvent, SetStateAction, useRef, useState } from "react";

const ACCEPTED_EXT = [".zip"];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `El archivo no puede superar los ${MAX_FILE_SIZE_MB}MB.`;
  }
  const ext = "." + file.name.split(".").pop()?.toLowerCase();
  if (!ACCEPTED_EXT.includes(ext)) {
    return "Sólo se permiten archivos .ZIP.";
  }
  return null;
}

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onAccept: (file?: File) => void;
}

export const AttachPrescriptionModal = ({ open, setOpen, onAccept }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (f: File) => {
    const error = validateFile(f);
    if (error) {
      setFileError(error);
      setFile(undefined);
    } else {
      setFileError(null);
      setFile(f);
    }
  };

  const clearFile = () => {
    setFile(undefined);
    setFileError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleClose = () => {
    setFile(undefined);
    setFileError(null);
    if (inputRef.current) inputRef.current.value = "";
    setOpen(false);
  };

  const handleAccept = () => {
    onAccept(file);
    setFile(undefined);
    setFileError(null);
    if (inputRef.current) inputRef.current.value = "";
    setOpen(false);
  };

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content minW="480px">
              <Dialog.Header paddingY="16px" paddingX="24px">
                <Dialog.Title fontWeight={600} fontSize="18px">
                  Adjuntar archivo de prescripción
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body paddingX="24px" paddingY="8px">
                <Flex flexDir="column" gap="16px">
                  <Box
                    border="2px dashed"
                    borderColor={dragging ? AppColors.GREEN_PRIMARY : "gray.300"}
                    borderRadius="8px"
                    height="140px"
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    cursor="pointer"
                    bg={dragging ? "green.50" : "gray.50"}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => inputRef.current?.click()}
                  >
                    <input
                      ref={inputRef}
                      type="file"
                      accept={ACCEPTED_EXT.join(",")}
                      style={{ display: "none" }}
                      onChange={handleInputChange}
                    />
                    {file ? (
                      <>
                        <Box
                          as="button"
                          position="absolute"
                          top="10px"
                          right="10px"
                          cursor="pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearFile();
                          }}
                        >
                          <X size={20} color={AppColors.GRAY_TEXT} />
                        </Box>
                        <Text fontSize="14px" fontWeight={500} paddingX="20px">
                          {file.name}
                        </Text>
                      </>
                    ) : (
                      <Flex flexDir="column" alignItems="center" gap="8px">
                        <Upload size={32} color={AppColors.GRAY_TEXT} />
                        <Text fontSize="14px" color={AppColors.GRAY_TEXT}>
                          Arrastrá el archivo o hacé click
                        </Text>
                        <Text fontSize="12px" color={AppColors.LIGHT_GRAY_TEXT}>
                          .ZIP hasta {MAX_FILE_SIZE_MB}MB
                        </Text>
                      </Flex>
                    )}
                  </Box>

                  <Box h="18px">
                    {fileError ? (
                      <Text fontSize="13px" color={AppColors.ERROR_TEXT}>
                        {fileError}
                      </Text>
                    ) : (
                      <Text fontSize="12px" color={AppColors.LIGHT_GRAY_TEXT}>
                        Opcional: podés confirmar sin adjuntar archivo.
                      </Text>
                    )}
                  </Box>
                </Flex>
              </Dialog.Body>

              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Button
                  variant="outline"
                  paddingX="16px"
                  fontWeight={600}
                  onClick={handleClose}
                >
                  Cancelar
                </Button>
                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={handleAccept}
                  disabled={!!fileError}
                >
                  Aceptar
                </Button>
              </Dialog.Footer>

              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
